import React, { useState } from 'react';
import { StarRating } from '../StarRating';
import { COLORS } from '../../../lib/styleGuide';

/**
 * ReviewForm Component
 * 
 * A form component for submitting product reviews with star rating
 * and optional comment. Handles validation and submission.
 * 
 * @param {Object} props
 * @param {Object} props.product - Product data
 * @param {string} props.product.id - Product ID
 * @param {string} props.product.name - Product name
 * @param {string} props.product.image - Product image URL
 * @param {function} props.onSubmit - Callback when review is submitted
 * @param {function} props.onCancel - Callback when form is cancelled
 * @param {Object} props.existingReview - Existing review data (for editing)
 * @param {boolean} props.isSubmitting - Whether form is currently submitting
 */
export function ReviewForm({
  product,
  onSubmit,
  onCancel,
  existingReview = null,
  isSubmitting = false
}) {
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [comment, setComment] = useState(existingReview?.comment || '');
  const [errors, setErrors] = useState({});
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    
    if (rating === 0) {
      newErrors.rating = 'Please select a rating';
    }
    
    if (comment.length > 500) {
      newErrors.comment = 'Comment must be 500 characters or less';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      onSubmit({
        productId: product.id,
        rating,
        comment: comment.trim()
      });
    }
  };
  
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };
  
  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid #E5E7EB',
      maxWidth: '500px',
      width: '100%'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px'
      }}>
        {product.image && (
          <img
            src={product.image}
            alt={product.name}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '8px',
              objectFit: 'cover'
            }}
          />
        )}
        <div>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: COLORS.NEUTRAL_TEXT,
            margin: 0
          }}>
            {product.name}
          </h3>
          <p style={{
            fontSize: '14px',
            color: COLORS.ACCENT_GRAY,
            margin: '4px 0 0 0'
          }}>
            {existingReview ? 'Edit your review' : 'Rate this product'}
          </p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit}>
        {/* Star Rating */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: COLORS.NEUTRAL_TEXT,
            marginBottom: '8px'
          }}>
            Rating *
          </label>
          
          <StarRating
            rating={rating}
            onRatingChange={setRating}
            interactive={true}
            size="large"
            showLabel={true}
          />
          
          {errors.rating && (
            <p style={{
              fontSize: '12px',
              color: COLORS.ERROR_RED,
              margin: '4px 0 0 0'
            }}>
              {errors.rating}
            </p>
          )}
        </div>
        
        {/* Comment */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: COLORS.NEUTRAL_TEXT,
            marginBottom: '8px'
          }}>
            Comment (optional)
          </label>
          
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your experience with this product..."
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '12px',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
              transition: 'border-color 0.2s ease'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = COLORS.PRIMARY_BROWN;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#E5E7EB';
            }}
          />
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '4px'
          }}>
            {errors.comment && (
              <p style={{
                fontSize: '12px',
                color: COLORS.ERROR_RED,
                margin: 0
              }}>
                {errors.comment}
              </p>
            )}
            <span style={{
              fontSize: '12px',
              color: COLORS.ACCENT_GRAY,
              marginLeft: 'auto'
            }}>
              {comment.length}/500
            </span>
          </div>
        </div>
        
        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            style={{
              padding: '10px 20px',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              background: 'white',
              color: COLORS.NEUTRAL_TEXT,
              fontSize: '14px',
              fontWeight: '500',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.5 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting || rating === 0}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: rating === 0 ? '#E0E0E0' : COLORS.PRIMARY_BROWN,
              color: rating === 0 ? '#999' : 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: (isSubmitting || rating === 0) ? 'not-allowed' : 'pointer',
              opacity: (isSubmitting || rating === 0) ? 0.5 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {isSubmitting ? 'Submitting...' : (existingReview ? 'Update Review' : 'Submit Review')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ReviewForm;
